const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    display: inline-block;
    font-family: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    vertical-align: middle;
  }

  .wrap{
    width: var(--sz);
    height: var(--sz);
    border-radius: var(--rad);
    overflow: hidden;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;

    border: var(--ctl-border-width-thin) solid var(--ctl-color-border);
    background: color-mix(in srgb, var(--ctl-color-border) 55%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-text) 85%, var(--ctl-color-muted));

    user-select: none;
  }

  /* sizes */
  :host([size="xs"]) { --sz: var(--ctl-size-avatar-xs); --fs: var(--ctl-font-size-2xs); }
  :host([size="sm"]) { --sz: var(--ctl-size-avatar-sm); --fs: var(--ctl-font-size-sm); }
  :host(:not([size]), [size="md"]) { --sz: var(--ctl-size-avatar-md); --fs: var(--ctl-font-size-md); }
  :host([size="lg"]) { --sz: var(--ctl-size-avatar-lg); --fs: var(--ctl-font-size-lg); }
  :host([size="xl"]) { --sz: var(--ctl-size-avatar-xl); --fs: var(--ctl-font-size-xl); }

  /* shape */
  :host(:not([shape]), [shape="circle"]) { --rad: var(--ctl-radius-full); }
  :host([shape="rounded"]) { --rad: var(--ctl-radius-lg); }

  img{
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: none; /* toggled on successful load */
  }

  .fallback{
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    font-weight: var(--ctl-font-weight-black);
    font-size: var(--fs);
    letter-spacing: var(--ctl-tracking-wide);
    text-transform: uppercase;
  }

  .muted{
    color: var(--ctl-color-muted);
    font-weight: var(--ctl-font-weight-extrabold);
  }

  /* subtle inner highlight */
  .shine{
    position:absolute;
    inset:0;
    pointer-events:none;
    background: linear-gradient(
      180deg,
      var(--ctl-color-shine),
      transparent 55%
    );
    opacity: var(--ctl-opacity-shine);
  }
</style>

<span class="wrap" part="wrap" aria-hidden="true">
  <img id="img" part="img" />
  <span id="fallback" class="fallback" part="fallback"></span>
  <span class="shine" part="shine"></span>
</span>
`;

function initialsFromName(name) {
  const s = String(name || "").trim();
  if (!s) return "";
  // Split on whitespace and punctuation-ish separators
  const parts = s.split(/[\s._-]+/).filter(Boolean);
  if (!parts.length) return "";
  const first = parts[0][0] || "";
  const last = (parts.length > 1 ? parts[parts.length - 1][0] : (parts[0][1] || "")) || "";
  return (first + last).toUpperCase();
}

export class CtlAvatar extends HTMLElement {
  static get observedAttributes() {
    return ["src", "alt", "name", "size", "shape"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._img = this.shadowRoot.getElementById("img");
    this._fallback = this.shadowRoot.getElementById("fallback");

    this._onLoad = this._onLoad.bind(this);
    this._onError = this._onError.bind(this);
  }

  connectedCallback() {
    // defaults
    if (!this.hasAttribute("size")) this.setAttribute("size", "md");
    if (!this.hasAttribute("shape")) this.setAttribute("shape", "circle");

    this._img.addEventListener("load", this._onLoad);
    this._img.addEventListener("error", this._onError);

    this._sync();
  }

  disconnectedCallback() {
    this._img.removeEventListener("load", this._onLoad);
    this._img.removeEventListener("error", this._onError);
  }

  attributeChangedCallback() {
    this._sync();
  }

  // attrs
  get src() { return this.getAttribute("src") || ""; }
  set src(v) { v == null || v === "" ? this.removeAttribute("src") : this.setAttribute("src", String(v)); }

  get alt() { return this.getAttribute("alt") || ""; }
  set alt(v) { v == null ? this.removeAttribute("alt") : this.setAttribute("alt", String(v)); }

  get name() { return this.getAttribute("name") || ""; }
  set name(v) { v == null ? this.removeAttribute("name") : this.setAttribute("name", String(v)); }

  get size() { return this.getAttribute("size") || "md"; }
  set size(v) { this.setAttribute("size", String(v || "md")); }

  get shape() { return this.getAttribute("shape") || "circle"; }
  set shape(v) { this.setAttribute("shape", String(v || "circle")); }

  _sync() {
    // normalize size/shape
    const size = this.size;
    if (!["xs","sm","md","lg","xl"].includes(size)) this.setAttribute("size", "md");

    const shape = this.shape;
    if (!["circle","rounded"].includes(shape)) this.setAttribute("shape", "circle");

    const src = this.getAttribute("src");
    const alt = this.getAttribute("alt") || "";
    const name = this.getAttribute("name") || "";

    // a11y: host acts like an image (when meaningful)
    // If src present, alt should be meaningful or empty for decorative.
    // If no src, use name/alt for label if present.
    const label = alt || name || "";
    if (label) {
      this.setAttribute("role", "img");
      this.setAttribute("aria-label", label);
    } else {
      // decorative
      this.removeAttribute("role");
      this.setAttribute("aria-hidden", "true");
      this.removeAttribute("aria-label");
    }

    // fallback initials
    const initials = initialsFromName(name || alt);
    this._fallback.textContent = initials || "•";
    this._fallback.classList.toggle("muted", !initials);

    // set img attrs & attempt load
    if (src) {
      // Reset before setting to force reload if same URL toggles
      this._img.style.display = "none";
      this._fallback.style.display = "inline-flex";

      this._img.alt = alt; // can be "" if decorative
      this._img.src = src;
    } else {
      // no src => fallback only
      this._img.removeAttribute("src");
      this._img.style.display = "none";
      this._fallback.style.display = "inline-flex";
    }
  }

  _onLoad() {
    // Show image, hide fallback
    this._img.style.display = "block";
    this._fallback.style.display = "none";
  }

  _onError() {
    // Broken image => fallback
    this._img.style.display = "none";
    this._fallback.style.display = "inline-flex";
  }
}

customElements.define("ctl-avatar", CtlAvatar);
